/**
 * Optional CDN image transformer.
 *
 * If `VITE_CF_IMAGE_BASE` is set (Cloudflare Images Resizing endpoint or
 * compatible service like Imgix/Cloudinary), `cdnImage(url, opts)` rewrites
 * the URL to request a width-optimized variant. Otherwise it returns the URL
 * unchanged so dev/preview keeps working without configuration.
 *
 * Example value for Cloudflare:
 *   VITE_CF_IMAGE_BASE=https://cdn.nutrimarket.shop/cdn-cgi/image
 * Yields:
 *   https://cdn.nutrimarket.shop/cdn-cgi/image/width=480,quality=75,format=auto/<src>
 */
const BASE = (import.meta.env.VITE_CF_IMAGE_BASE as string | undefined)?.replace(/\/$/, "");

export interface CdnImageOpts {
  width?: number;
  quality?: number;
  format?: "auto" | "webp" | "avif";
}

export function cdnImage(src: string | undefined | null, opts: CdnImageOpts = {}): string {
  if (!src) return "";
  if (!BASE) return src;
  // Don't touch data URIs or already-rewritten paths.
  if (/^data:|^blob:/.test(src) || src.includes("/cdn-cgi/image/")) return src;
  const params = [
    opts.width ? `width=${opts.width}` : null,
    `quality=${opts.quality ?? 75}`,
    `format=${opts.format ?? "auto"}`,
  ].filter(Boolean).join(",");
  return `${BASE}/${params}/${src}`;
}

/** Build a srcset string at common breakpoints for responsive `<img srcset>`. */
export function cdnSrcSet(src: string | undefined | null, widths: number[] = [240, 480, 720, 960]): string {
  if (!src || !BASE) return "";
  return widths.map((w) => `${cdnImage(src, { width: w })} ${w}w`).join(", ");
}